import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import io from "socket.io-client";
import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useGetUnreadMessagesQuery, useMarkMessagesAsReadMutation } from "../../../Api/apiSlice";
import { setNotification } from "../../../Api/notificationSlice";
import { useDispatch } from "react-redux";
const socket = io(process.env.REACT_APP_API_URL); // Replace with your backend URL

const ChatCopy = () => {
  const { data: unreadMessages, isLoading: isUnreadMessagesLoading, refetch } = useGetUnreadMessagesQuery();
  const dispatch = useDispatch();
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [users, setUsers] = useState([]); // Store users (drivers/customers)
  const userId = localStorage.getItem("userId");
  const organizationId = localStorage.getItem("organizationId");
  const token = localStorage.getItem("authToken");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const userName = localStorage.getItem("userName");
  const [selectedUserName, setSelectedUserName] = useState(null);
  const [markMessagesAsRead] = useMarkMessagesAsReadMutation();

  // Fetch users (drivers/customers) for admin
  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/api/admin/organization/users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUsers(response.data.users); // Set users to the state
    } catch (error) {
      console.error("Error fetching users:", error.message);
    }
  };

  const initializeChat = async (selectedUserId, selectedUserName) => {
    try {
      setSelectedUserId(selectedUserId);
      setSelectedUserName(selectedUserName);
      const response = await axios.post(
        `${apiUrl}/api/admin/chat`,
        {
          userId: selectedUserId,
          organizationId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const chat = response.data.chat;
      setChatId(chat.id);

      if (chat.id) {
        markMessagesAsRead(chat.id);
      }
      // dispatch(setNotification(unreadMessages.hasUnread));
      const result = await refetch();
      dispatch(setNotification(result.data?.hasUnread));

      // dispatch(setNotification(false));
      socket.emit("join_chat", chat.id); // Join the specific chat room
      socket.emit("admin_connect", organizationId); // Join organization-wide room
    } catch (error) {
      console.error("Error initializing chat:", error.message);
    }
  };

  const fetchMessages = async (chatId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/api/admin/chat/${chatId}/messages`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setMessages(response.data.messages); // Fetch and set messages for the current chat
    } catch (error) {
      console.error("Error fetching messages:", error.message);
    }
  };

  useEffect(() => {
    fetchUsers(); // Fetch users when the component is mounted
  }, []);
  useEffect(() => {
    socket.on("new_message_notification", (data) => {
      const { chatId: incomingChatId, message } = data;

      // If the message is for the active chat, append it to the messages
      if (incomingChatId === chatId) {
        setMessages((prev) => [...prev, message]);
      } else {
        // If it's for another chat, handle the notification logic
        if (Notification.permission === "granted") {
          new Notification("New Message", {
            body: `${message.content}`,
          });
        }

        // Optionally, show an unread badge for the chat
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.chatId === incomingChatId
              ? { ...user, unread: (user.unread || 0) + 1 }
              : user
          )
        );
      }
    });

    return () => {
      socket.off("new_message_notification");
    };
  }, [chatId]);

  useEffect(() => {
    if (chatId) fetchMessages(chatId); // Fetch messages when chatId is set
  }, [chatId]);

  useEffect(() => {
    socket.on("new_message", (data) => {
      //   setMessages((prev) => [...prev, data.message]); // Append new message to state
      if (data.chatId === chatId) {
        // Append message only if it belongs to the current chat
        setMessages((prev) => [...prev, data.message]);
      }
      return;
    });

    return () => {
      socket.off("new_message");
    };
  }, [chatId]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    // Emit to backend for message broadcast to the customer
    socket.emit("send_message", {
      chatId,
      content: newMessage,
      senderId: userId,
      isAdmin: true,
      organizationId,
    });
    setNewMessage("");
  };
  const messagesEndRef = useRef(null);

  // Scroll to the last message when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);
  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      height="calc(100vh - 5.4rem)"
      sx={{ backgroundColor: "#f5f5f5", marginTop: "5.4rem" }}
    >
      {/* User List */}
      <Paper
        elevation={2}
        sx={{
          width: { xs: "100%", md: "25%" },
          height: { xs: "auto", md: "100%" },
          overflowY: "auto",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            p: 2,
            borderBottom: "1px solid #ddd",
            backgroundColor: "#F38712",
            color: "white",
          }}
        >
          Users
        </Typography>
        <List>
          {users?.map((user) => (
            <ListItem key={user.id} disablePadding>
              <ListItemButton
                onClick={() => initializeChat(user?.id, user?.name)}
                sx={{
                  backgroundColor:
                    selectedUserId === user.id ? "#e0e0e0" : "inherit",
                }}
              >
                <Avatar sx={{ mr: 2 }}>{user.name[0]}</Avatar>
                <ListItemText primary={user.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Chat Box */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        sx={{
          backgroundColor: "#fff",
          borderLeft: { md: "1px solid #ddd" },
          borderRight: { md: "1px solid #ddd" },
        }}
      >
        {/* Messages */}
        <Box
          flex={1}
          sx={{
            p: 2,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {messages.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                alignSelf: msg.isAdmin ? "flex-end" : "flex-start",
                maxWidth: "70%",
                borderRadius: 2,
                padding: 1.5,
                backgroundColor: msg.isAdmin ? "#1976d2" : "#f1f0f0",
                color: msg.isAdmin ? "#fff" : "#000",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {msg.isAdmin ? userName : selectedUserName}
              </Typography>
              <Typography variant="body2">{msg.content}</Typography>
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input and Send Button */}
        {chatId && (
          <Box
            display="flex"
            alignItems="center"
            p={2}
            sx={{
              borderTop: "1px solid #ddd",
            }}
          >
            <TextField
              fullWidth
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              variant="outlined"
              size="small"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendMessage}
              sx={{ ml: 2 }}
              endIcon={<SendIcon />}
            >
              Send
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatCopy;
